/* @flow */
import { ActionTypes } from 'constants/OrganizationConstants';
import apiRequest from 'lib/apiRequest';
import CurrentUser from 'auth/CurrentUser';
import { dispatcher } from 'lib/Dispatcher';
import GroupActions from 'groups/actions/GroupActions';
import { isMember, needsEmailConf } from 'shapes/Affiliation';
import OrganizationActions from 'actions/OrganizationActions';
import ToastActions from 'actions/ToastActions';
import isAPIError from 'lib/isAPIError';

import type { OrganizationRole } from 'queries/__generated__/globalGQLTypes';

export function adminOptIn(orgId: Backbone$Id, orgName: string, affiliation: Object) {
  const userId = CurrentUser.getUserId();

  const url = `/organizations/${orgId}/users/${userId}/roles/admin`;
  apiRequest(url, 'PATCH', { opted_in: true })
    .then(() => {
      OrganizationActions.loadUserOrganizations();
      // Fetch the staff class that the user has access to now they they joined the org
      GroupActions.loadGroups();
      if (affiliation && needsEmailConf(affiliation)) {
        ToastActions.showSuccessToast('Please confirm your email address');
      } else {
        ToastActions.showSuccessToast(
          `You are now a verified administrator for ${orgName} on Remind`
        );
      }
    })
    .catch(e => {
      ToastActions.showDefaultErrorToast();
      console.warn(e, e && e.stack);
    });
}

export function adminOptOut(orgId: Backbone$Id, orgName: string, affiliation: Object) {
  const userId = CurrentUser.getUserId();

  const url = `/organizations/${orgId}/users/${userId}/roles/admin`;
  apiRequest(url, 'PATCH', { opted_in: false })
    .then(() => {
      // Stop showing the organization if they weren't a member before
      if (affiliation && !isMember(affiliation)) {
        OrganizationActions.removeOrganization(orgId);
      } else if (affiliation) {
        // update the affiliation to remove the pending role
        OrganizationActions.loadUserOrganizations();
      }
      ToastActions.showSuccessToast(
        `You chose not to be an administrator for ${orgName} on Remind`
      );
    })
    .catch(e => {
      ToastActions.showDefaultErrorToast();
      console.warn(e, e && e.stack);
    });
}

export function resignAdminship(orgId: Backbone$Id, userId: Backbone$Id) {
  const url = `/organizations/${orgId}/users/${userId}/roles/admin`;
  return apiRequest(url, 'DELETE').then(() => {
    OrganizationActions.loadUserOrganizations();
    dispatcher.dispatch({
      type: ActionTypes.REMOVE_ADMIN_ROLE,
      orgId,
    });
  });
}

async function patchOrganizationMemberRoles(
  organizationId: number,
  userId: Backbone$Id,
  roles: OrganizationRole[]
) {
  const url = `/organizations/${organizationId}/users/${userId}/roles`;
  return apiRequest(url, 'PATCH', { roles });
}

export async function patchCurrentUserRoles(
  organizationId: number,
  currentUserId: number,
  roles: OrganizationRole[]
) {
  try {
    const affiliation = await patchOrganizationMemberRoles(organizationId, currentUserId, roles);

    dispatcher.dispatch({
      type: ActionTypes.UPDATE_CURRENT_USER_ORGANIZATION_AFFILIATION,
      affiliation,
      organizationId,
    });
  } catch (e) {
    if (!isAPIError(e)) {
      console.warn(e, e && e.stack);
      console.error('failed to patchCurrentUserRoles');
    }
    ToastActions.showDefaultErrorToast();
  }
}

export async function patchRoles(
  organizationId: number,
  userId: Backbone$Id,
  roles: OrganizationRole[]
) {
  try {
    const affiliation = await patchOrganizationMemberRoles(organizationId, userId, roles);

    dispatcher.dispatch({
      type: ActionTypes.UPDATE_ORG_AFF_VIEW,
      orgAffView: affiliation,
      orgId: organizationId,
      userId,
    });

    ToastActions.showSuccessToast('Saved');
  } catch (e) {
    if (!isAPIError(e)) {
      console.error(e);
    }
    ToastActions.showDefaultErrorToast();
  }
}
